import {
  getCoreRowModel,
  useVueTable,
  createColumnHelper,
  useRowSelect,
  getExpandedRowModel,
  getSortedRowModel,
  setSorting,
} from '@tanstack/vue-table';
import merge from 'lodash/merge';
import { disabledSortingConfig, tableColumnConfigDefaults } from '@src/components/Table/constants';
import { getSortingColumnsCount, hasSortingDisabledKey } from '@src/components/Table/utils/sorting';

export function getSortingProps(column, apiSorting) {
  const sortingKey = column?.sortingFn;
  if (hasSortingDisabledKey(sortingKey)) return { ...disabledSortingConfig };

  if (apiSorting) {
    return {
      sortingFn: column?.sortingFn,
      enableSorting: !!column?.sortingFn,
      sortingState: column?.sortingState,
    };
  }

  const sortingProps = merge({}, {
    sortingFn: column?.sortingFn,
    enableSorting: !!column?.sortingFn,
    sortingState: column?.sortingState,
  }, tableColumnConfigDefaults);

  return sortingProps;
}

/** This object tells what sections the table is supposed to have because the library always seems to render the sections
 even if they're not defined */
export const tableHasSections = {
  hasHeader: false,
  hasFooter: false,
};

function mapTableColumns({ columns, hasSubRows, shouldShowSortingOnHover, apiSorting }) {
  const columnHelper = createColumnHelper();

  return columns?.map((column, index) => {
    if (column?.name) {
      tableHasSections.hasHeader = true;
    }

    if (column?.footer) {
      tableHasSections.hasFooter = true;
    }

    /** This is for the case of header groups above the actual table headers. */
    if (!column?.field && column?.columns?.length > 0) {
      return columnHelper.group({
        index,
        header: column?.name ?? '',
        isHeaderSFC: column?.isHeaderSFC,
        name: column?.name ?? '',
        footer: column?.footer,
        id: column?.id,
        isSFC: column?.isSFC,
        cell: column?.cell,
        class: column?.class,
        size: column?.size,
        meta: column?.meta,
        minSize: column?.minSize,
        maxSize: column?.maxSize,
        colSpan: column?.colSpan,
        inputType: column?.inputType,
        inputAttributes: column?.inputAttributes,
        columns: column?.columns
          ? mapTableColumns({ columns: column?.columns, hasSubRows, shouldShowSortingOnHover, apiSorting }) : null,
      });
    }

    if (typeof column?.field === 'function' && !column?.id) {
      throw new Error('If the field property in a column definitions is a function. Then an id for that column definition must also be used.');
    }

    if (!column?.field) {
      throw new Error('If the column definition does not contain a columns property. Then the field property in the column definition must be set to an actual field in the bound data set.');
    }

    const classObj = column?.class || {};
    if (!shouldShowSortingOnHover) {
      classObj.header = `${classObj.header || ''} th-header-sorting-always-show`;
    }

    return columnHelper.accessor(column?.field, {
      index,
      header: column?.header ?? column?.name ?? '',
      name: column?.name ?? '',
      isHeaderSFC: column?.isHeaderSFC,
      footer: column?.footer,
      id: column?.id,
      isSFC: column?.isSFC,
      cell: column?.cell,
      class: classObj,
      size: column?.size,
      meta: column?.meta,
      minSize: column?.minSize,
      maxSize: column?.maxSize,
      colSpan: column?.colSpan,
      inputType: column?.inputType,
      inputAttributes: column?.inputAttributes,
      ...getSortingProps(column, apiSorting),
      columns: column?.columns
        ? mapTableColumns({ columns: column?.columns, hasSubRows, shouldShowSortingOnHover }) : null,
    });
  });
}

function removeFalseyProperties(columns) {
  return columns.map((column) => {
    const baseColumn = Object.fromEntries(Object.entries(column).filter(([, v]) => v != null));

    if (baseColumn?.columns?.length > 0) {
      // eslint-disable-next-line no-param-reassign
      baseColumn.columns = removeFalseyProperties(column.columns);
    }

    return baseColumn;
  });
}

export const getFormattedColumnDefinitions = (
  columns,
  initialData,
  minSortingIconVisibleOnHoverPercentage,
  apiSorting,
) => {
  const { totalColumnsCount, sortingColumnsCount } = getSortingColumnsCount(columns);
  const shouldShowSortingOnHover = totalColumnsCount > 0
    ? ((sortingColumnsCount / totalColumnsCount) * 100) >= minSortingIconVisibleOnHoverPercentage : false;

  const mappedColumns = mapTableColumns({
    columns,
    hasSubRows: initialData.some((record) => record.subRows),
    shouldShowSortingOnHover,
    apiSorting,
  });

  /** Remove all properties that are falsey from the column definitions because the library does not play well with them */
  return removeFalseyProperties(mappedColumns);
};

function createExpandableRowsTable({
  data: initialData,
  columns: initialColumns,
  eventCallbacks = {},
  preselectedKeys = [],
  selectionType = null,
  minSortingIconVisibleOnHoverPercentage,
  apiSorting = false,
}) {
  if (!(minSortingIconVisibleOnHoverPercentage >= 0 && minSortingIconVisibleOnHoverPercentage <= 100)) {
    throw new Error('Sorting icon threshold value is not valid. It must be between [0,100]');
  }
  const selectedRowIds = preselectedKeys.reduce((p, c) => {
    // eslint-disable-next-line no-param-reassign
    p[c] = true;
    return p;
  }, {});

  return useVueTable({
    get data() {
      return initialData;
    },
    get columns() {
      return getFormattedColumnDefinitions(
        initialColumns,
        initialData,
        minSortingIconVisibleOnHoverPercentage,
        apiSorting,
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getSubRows: (row) => row.subRows,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    useRowSelect,
    enableRowSelection: typeof selectionType === 'function' ? selectionType : selectionType != null,
    autoResetSelectedRows: false,
    getRowId: (row) => row.key,
    initialState: {
      rowSelection: selectedRowIds,
      expanded: selectedRowIds,
    },
    meta: {
      ...eventCallbacks,
    },
  });
}

export { createExpandableRowsTable };
